"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppLogo from "@/components/ui/AppLogo";

interface FieldErrors {
  fullName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-emerald-900/90 border border-emerald-500/40 text-emerald-300 text-sm px-5 py-3.5 rounded-lg shadow-xl backdrop-blur-md animate-fade-in">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 text-emerald-400/60 hover:text-emerald-300 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ["Weak", "Fair", "Good", "Strong"];
  const colors = ["bg-red-500", "bg-yellow-500", "bg-blue-400", "bg-emerald-400"];
  const textColors = ["text-red-400", "text-yellow-400", "text-blue-400", "text-emerald-400"];
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i < score ? colors[score - 1] : "bg-cream/10"
            }`}
          />
        ))}
      </div>
      <p className={`text-xs ${textColors[score - 1] ?? "text-cream/40"}`}>
        Password strength: {labels[score - 1] ?? ""}
      </p>
    </div>
  );
}

export default function SignupPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    password: "",
    confirmPassword: "",
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "fullName":
        if (!value.trim()) return "Full name is required.";
        if (value.trim().length < 2) return "Name must be at least 2 characters.";
        return "";
      case "email":
        if (!value.trim()) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
        return "";
      case "password":
        if (!value) return "Password is required.";
        if (value.length < 6) return "Password must be at least 6 characters.";
        return "";
      case "confirmPassword":
        if (!value) return "Please confirm your password.";
        if (value !== formData.password) return "Passwords do not match.";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
    // Re-validate confirmPassword when password changes
    if (name === "password" && touched["confirmPassword"]) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword:
          formData.confirmPassword && formData.confirmPassword !== value
            ? "Passwords do not match."
            : "",
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = { fullName: true, email: true, password: true, confirmPassword: true };
    setTouched(allTouched);
    const errors: FieldErrors = {
      fullName: validateField("fullName", formData.fullName),
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
      confirmPassword: validateField("confirmPassword", formData.confirmPassword),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some((e) => e)) return;

    setLoading(true);
    setTimeout(() => {
      const user = {
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company,
      };
      sessionStorage.setItem("standave_user", JSON.stringify(user));
      sessionStorage.setItem("standave_auth", "true");
      setLoading(false);
      setToast("Account created successfully! Redirecting to dashboard…");
      setTimeout(() => router.push("/dashboard"), 1500);
    }, 1200);
  };

  const inputClass = (field: keyof FieldErrors) =>
    `w-full bg-navy/60 border text-cream placeholder-cream/30 px-4 py-3 rounded-sm text-sm focus:outline-none transition-colors ${
      touched[field] && fieldErrors[field]
        ? "border-red-500/60 focus:border-red-500" :"border-[rgba(201,168,76,0.2)] focus:border-gold/60"
    }`;

  return (
    <main className="bg-navy min-h-screen">
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
      <Header />
      <section className="min-h-screen flex items-center justify-center px-4 pt-28 pb-16">
        <div className="w-full max-w-md">
          <div className="bg-[#0C1B33]/80 backdrop-blur-xl border border-[rgba(201,168,76,0.18)] rounded-sm p-8 shadow-[0_8px_60px_rgba(0,0,0,0.5)]">
            <div className="flex justify-center mb-6">
              <AppLogo size={40} text="StanDave" iconName="BuildingOffice2Icon" className="text-cream" />
            </div>
            <h1 className="font-display text-2xl text-cream text-center mb-1">Create Your Account</h1>
            <p className="text-cream/50 text-sm text-center mb-8">Join StanDave LLC and access our services</p>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Full Name */}
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Full Name <span className="text-gold">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="John Doe"
                  className={inputClass("fullName")}
                />
                {touched.fullName && fieldErrors.fullName && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.fullName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Email Address <span className="text-gold">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="john@company.com"
                  className={inputClass("email")}
                />
                {touched.email && fieldErrors.email && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">Company Name</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company (optional)"
                  className="w-full bg-navy/60 border border-[rgba(201,168,76,0.2)] text-cream placeholder-cream/30 px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-gold/60 transition-colors"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Password <span className="text-gold">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Min. 6 characters"
                  className={inputClass("password")}
                />
                <PasswordStrength password={formData.password} />
                {touched.password && fieldErrors.password && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Confirm Password <span className="text-gold">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Re-enter password"
                  className={inputClass("confirmPassword")}
                />
                {touched.confirmPassword && fieldErrors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.confirmPassword}
                  </p>
                )}
                {touched.confirmPassword && !fieldErrors.confirmPassword && formData.confirmPassword && (
                  <p className="mt-1.5 text-xs text-emerald-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Passwords match
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3.5 text-sm font-display uppercase tracking-widest rounded-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <p className="text-cream/40 text-sm text-center mt-6">
              Already have an account?{" "}
              <Link href="/login" className="text-gold hover:text-gold/80 transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
